// Beslag en bewegingen in de viewer herkennen (beslag P5). Zonder DOM, zodat de tests het kunnen draaien
// (src/modules/3d/beslag.test.ts).
//
// - Een stuk beslag is een node met extras.soort "beslag" (zie ../beslag.ts). Het verschijnt zodra alle onderdelen
//   waar het aan zit er staan, net als een schroef, maar vliegt niet in.
// - Een beweging is een draaipunt-node met extras.soort "beweging". De viewer draait dat draaipunt om de as van
//   0 (dicht) tot max_deg (open). De rotatie in het GLB is de stand uit het ontwerp.

import { Quaternion, Vector3, type Object3D } from "three";

/** Het object van de beslagnode waar deze mesh bij hoort, of null. */
export function beslagNode(obj: Object3D): Object3D | null {
  for (let o: Object3D | null = obj; o; o = o.parent) {
    if (o.userData.soort === "beslag") return o;
    if (o.userData.partId !== undefined || o.userData.soort === "schroef" || o.userData.soort === "deuvel") return null;
  }
  return null;
}

export interface ViewerBeweging {
  node: Object3D;
  opId: string;
  /** `revolute` draait om `as` (graden), `prismatic` (lade) schuift langs `as` (mm). */
  kind: "revolute" | "prismatic";
  /** Draaias of schuifrichting in de assen van de viewer (glTF, Y omhoog), eenheidsvector. */
  as: Vector3;
  /** Grootste hoek in graden, of bij een lade de uittreklengte in mm. */
  maxDeg: number;
  /** De hoek nu in graden, of bij een lade de afstand in mm. */
  hoek: number;
  /** Alleen bij een lade: de plek van het draaipunt in de dichte stand (meters, viewer-assen). */
  basis: Vector3;
}

/** De bewegingen in het model, met de hoek uit het ontwerp (stand_deg). Een draaipunt zonder geldige as telt niet. */
export function bewegingenIn(root: Object3D): ViewerBeweging[] {
  const uit: ViewerBeweging[] = [];
  root.traverse((o) => {
    const e = o.userData as { soort?: unknown; opId?: unknown; kind?: unknown; as?: { dir?: unknown }; max_deg?: unknown; stand_deg?: unknown; max_mm?: unknown; stand_mm?: unknown };
    if (e.soort !== "beweging") return;
    const dir = e.as?.dir;
    if (!Array.isArray(dir) || dir.length !== 3 || !dir.every((v) => typeof v === "number" && Number.isFinite(v))) return;
    // Mate-wereld (Z omhoog) naar glTF (Y omhoog): (x, y, z) wordt (x, z, -y).
    const as = new Vector3(dir[0] as number, dir[2] as number, -(dir[1] as number));
    if (as.lengthSq() < 1e-12) return;
    as.normalize();
    if (e.kind === "prismatic") {
      // Een lade: het draaipunt staat stand_mm langs de as van zijn dichte plek.
      if (typeof e.max_mm !== "number" || !(e.max_mm > 0)) return;
      const hoek = typeof e.stand_mm === "number" ? e.stand_mm : 0;
      const basis = o.position.clone().addScaledVector(as, -hoek / 1000);
      uit.push({ node: o, opId: String(e.opId ?? o.name), kind: "prismatic", as, maxDeg: e.max_mm, hoek, basis });
      return;
    }
    if (typeof e.max_deg !== "number" || !(e.max_deg > 0)) return;
    uit.push({ node: o, opId: String(e.opId ?? o.name), kind: "revolute", as, maxDeg: e.max_deg, hoek: typeof e.stand_deg === "number" ? e.stand_deg : 0, basis: o.position.clone() });
  });
  return uit;
}

/**
 * Beslag en bewegingen in de viewer volgens de vlag (data-beweging, nu alleen in testmodus). Aan: de bewegingen in de
 * stand uit het ontwerp. Uit: zoals vóór beslag P5, dus beslag onzichtbaar, elke beweging dicht en geen bewegingen
 * voor de knop of het camerakader.
 */
export function bewegingenVoorVlag(root: Object3D, aan: boolean): ViewerBeweging[] {
  const bewegingen = bewegingenIn(root);
  if (aan) return bewegingen;
  for (const b of bewegingen) zetHoek(b, 0);
  root.traverse((o) => {
    if (o.userData.soort === "beslag") o.visible = false;
  });
  root.updateMatrixWorld(true);
  return [];
}

/** Zet het draaipunt op `graden` om zijn as, of bij een lade `graden` mm langs de as. */
export function zetHoek(b: ViewerBeweging, graden: number): void {
  b.hoek = graden;
  if (b.kind === "prismatic") b.node.position.copy(b.basis).addScaledVector(b.as, graden / 1000);
  else b.node.quaternion.copy(new Quaternion().setFromAxisAngle(b.as, (graden * Math.PI) / 180));
}

/** Zo lang duurt openen of sluiten van 90 graden (ms). Een grotere hoek duurt naar verhouding langer, tot 2 s. */
export const DRAAI_MS_PER_90 = 700;

export function draaiDuur(vanGraden: number, naarGraden: number, kind: ViewerBeweging["kind"] = "revolute"): number {
  // Een lade: 300 mm uittrekken duurt zo lang als een draai van 90 graden.
  const stap = kind === "prismatic" ? 300 : 90;
  return Math.min(2000, Math.max(200, (Math.abs(naarGraden - vanGraden) / stap) * DRAAI_MS_PER_90));
}

/** Staat het model open? Ja als minstens één beweging meer dan de helft open staat. */
export function staatOpen(bewegingen: readonly ViewerBeweging[]): boolean {
  return bewegingen.some((b) => b.hoek > b.maxDeg / 2);
}
